import type { GraphView } from '@/types/graph-view'
import { apiClient } from './client'

/** One external-source citation backing an OSINT report. */
export interface OsintSource {
  text?: string
  source_url?: string
  retrieved_at?: string
}

export type OsintRunStatus =
  | 'queued'
  | 'running'
  | 'awaiting_input'
  | 'finished_success'
  | 'finished_error'

/** One answered clarification question. */
export interface OsintClarificationAnswer {
  question: string
  answer: string
  /** Set when the user picked one of the offered records rather than typing. */
  selectedId?: string | null
}

/**
 * One register record the user can pick when the agent can't tell namesakes
 * apart. These are the register's own rows, captured when the agent asked — so
 * picking one yields an identifier that is verbatim from the source.
 */
export interface OsintClarificationOption {
  id: string
  label: string
  detail: string | null
  /**
   * 'register' = the register's own row (IČO / date of birth are verbatim from
   * the source). 'web' = someone the agent found on an open-web page and nobody
   * has verified exists as described — shown separately, and picking one yields
   * an identity the report must not call register-confirmed.
   */
  origin: 'register' | 'web'
  ico: string | null
  birthDate: string | null
  address: string | null
  recordUrl: string | null
  terminated: string | null
}

/** The agent's pending (or answered) request for a missing identifier. */
export interface OsintClarification {
  context: string | null
  questions: string[]
  /** Present only when the ask is "which of these records is the subject?". */
  options: OsintClarificationOption[] | null
  answers: OsintClarificationAnswer[] | null
  selected: OsintClarificationOption | null
  rounds: number
}

/**
 * One entity the run suggests importing into the case graph. Already conformed
 * to the case schema (layered over the OSINT baseline vocabulary) server-side —
 * `fromBaseline` marks a type the case schema itself does not declare.
 */
export interface OsintProposedEntity {
  key: string
  type: string
  name: string
  properties: Record<string, string>
  confidence: string | null
  sourceUrls: string[]
  note: string | null
  fromBaseline: boolean
}

/** One proposed relationship between two proposed entities. */
export interface OsintProposedRelation {
  key: string
  type: string
  sourceKey: string
  targetKey: string
  confidence: string | null
  sourceUrls: string[]
  note: string | null
  fromBaseline: boolean
}

/** The importable half of a finished report — entities + their relations. */
export interface OsintGraphProposal {
  entities: OsintProposedEntity[]
  relations: OsintProposedRelation[]
  schemaName: string | null
  generatedAt: string | null
}

export interface OsintRun {
  id: string
  investigationId: string
  agentThreadId: string | null
  status: OsintRunStatus
  reportMd: string | null
  sources: OsintSource[] | null
  clarification: OsintClarification | null
  graphProposal: OsintGraphProposal | null
  stepsCompleted: number
  stepsTotal: number | null
  errorMessage: string | null
  startedAt: string
  finishedAt: string | null
}

export type OsintInvestigationStatus = 'running' | 'awaiting_review' | 'closed'

export interface OsintInvestigation {
  id: string
  caseId: string
  title: string
  brief: string
  status: OsintInvestigationStatus
  authorId: string | null
  authorName: string
  createdAt: string
  updatedAt: string
  latestRun: OsintRun | null
}

export interface OsintCreateBody {
  title: string
  brief: string
}

/**
 * Launch an investigation for an entity already in the case graph. Only the
 * entity reference travels — the title and brief are composed server-side from
 * the node's stored properties, so internal case identifiers never reach the
 * external providers the run calls.
 */
export interface OsintCreateFromEntityBody {
  entityId: string
  instruction?: string
}

export interface OsintAnswerBody {
  answers: OsintClarificationAnswer[]
}

export const osintApi = {
  list: (caseId: string) => apiClient.get<OsintInvestigation[]>(`/v1/cases/${caseId}/osint`),

  get: (caseId: string, id: string) =>
    apiClient.get<OsintInvestigation>(`/v1/cases/${caseId}/osint/${id}`),

  create: (caseId: string, body: OsintCreateBody) =>
    apiClient.post<OsintInvestigation>(`/v1/cases/${caseId}/osint`, body),

  /** Start an investigation for a graph entity; the server composes the brief. */
  createFromEntity: (caseId: string, body: OsintCreateFromEntityBody) =>
    apiClient.post<OsintInvestigation>(`/v1/cases/${caseId}/osint/from-entity`, body),

  /** Answer the agent's pending clarification questions and resume the run. */
  answer: (caseId: string, id: string, body: OsintAnswerBody) =>
    apiClient.post<OsintInvestigation>(`/v1/cases/${caseId}/osint/${id}/answer`, body),

  /** Skip answering — finalize the report with whatever the agent already has. */
  finalizeNow: (caseId: string, id: string) =>
    apiClient.post<OsintInvestigation>(`/v1/cases/${caseId}/osint/${id}/finalize-now`, {}),

  /**
   * Write the selected proposed entities (and the relations between them) into
   * the case graph, and get back the written subgraph to merge into the active
   * investigation. Only the keys travel — the records come from the proposal the
   * run persisted. Omit `entityKeys` to import the whole proposal.
   */
  importProposal: (caseId: string, id: string, entityKeys?: string[]) =>
    apiClient.post<GraphView>(`/v1/cases/${caseId}/osint/${id}/import`, { entityKeys }),

  /** SSE URL for a live run's progress (register plan + step + finish events). */
  eventsUrl: (caseId: string, id: string) => `/api/v1/cases/${caseId}/osint/${id}/events`,
}

/** Live progress events streamed from the OSINT run's SSE endpoint. */
export type OsintRunEvent =
  | { type: 'run_status'; status?: string; steps_completed?: number; steps_total?: number | null }
  | { type: 'plan'; todos?: unknown }
  | { type: 'step_started'; tool_name?: string; steps_completed?: number }
  | { type: 'subagent_activity'; tool_name?: string; steps_completed?: number }
  | { type: 'clarification_requested'; run_id?: string }
  | { type: 'run_finished'; status?: string; error?: string | null }
